'use client';

import type { FC, ImgHTMLAttributes } from 'react';
import { useState } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { getAvatarGradient, getAvatarInitials } from './avatarFallback';

const avatarVariants = cva(
  'inline-flex items-center justify-center rounded-full overflow-hidden shrink-0 select-none',
  {
    variants: {
      size: {
        s: 'size-8 text-[13px]',
        m: 'size-12 text-[18px]',
        l: 'size-16 text-[24px]',
        xl: 'size-24 text-[36px]',
      },
    },
    defaultVariants: {
      size: 'm',
    },
  },
);

interface AvatarProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'size'> {
  size?: 's' | 'm' | 'l' | 'xl';
  /** Stable key — userId or name — for picking a deterministic peer-color fallback. */
  fallbackKey?: string | number;
  firstName?: string | null;
  lastName?: string | null;
  /** Legacy single-character fallback. Kept for back-compat; ignored when firstName/lastName is set. */
  fallback?: string;
}

export const Avatar: FC<AvatarProps> = ({
  size = 'm',
  fallback,
  fallbackKey,
  firstName,
  lastName,
  className,
  src,
  alt,
  ...props
}) => {
  const [hasError, setHasError] = useState(false);
  const baseClasses = cn(avatarVariants({ size }), className);

  if (!src || hasError) {
    const initials = getAvatarInitials(firstName, lastName);
    const key = fallbackKey ?? (`${firstName ?? ''}${lastName ?? ''}` || 'fallback');
    return (
      <div
        className={cn(baseClasses, 'text-white font-semibold')}
        style={{ background: getAvatarGradient(key) }}
      >
        {initials || fallback || '👤'}
      </div>
    );
  }

  return (
    <img
      className={baseClasses}
      src={src}
      alt={alt || 'Avatar'}
      loading="lazy"
      decoding="async"
      onError={() => setHasError(true)}
      {...props}
    />
  );
};
